import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import { StatusCode } from "api/protocols";

import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { IFacilityState } from "redux/reducers/facility";
import "../../facility/facility.scss";
import "./shotgunTemplate.scss";

import { useTranslation } from "react-i18next";
import Checkbox from "components/form/checkbox/Checkbox";
import {
  IShotgunTemplateGroup,
  PostShotGunTemplate,
  PutShotGunTemplateGenerate,
} from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";

type ShotgunDirection = "regular" | "reverse";

interface IShotgunHoleRow extends Array<IShotgunTemplateGroup> {
  checked?: boolean;
  holeNumber?: number;
}

interface IShotGunTemplate {
  title: string;
  holes: number;
  direction: ShotgunDirection;
  courseId: number | null;
  groups: IShotgunTemplateGroup[];
  groupsFormatted: IShotgunHoleRow[];
  selectedHoles: any[];
  selectAllHoles: boolean;
}

interface IProps {
  facilityStore: IFacilityState;
}

function shotgunHoleLabelMaxForCount(holes: number): number {
  if (holes === 27) {
    return 9;
  }
  if (holes === 36 || holes === 54 || holes === 72) {
    return 18;
  }
  return holes > 0 ? holes : 18;
}

export default function ShotGunTemplateNew(props: IProps) {
  const { facilityStore } = props;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const { Option } = Select;
  const playableCourses = (facilityStore?.facility?.courses ?? []).filter(
    course => !course.scoring_only && (course.holes ?? 0) > 0,
  );
  const coursesDiffer = new Set(playableCourses.map(course => course.holes)).size > 1;

  const history = useHistory();
  const { t } = useTranslation();
  const [state, setState] = useState<IShotGunTemplate>({
    title: "",
    holes: 1,
    direction: "regular",
    courseId: null,
    groups: [],
    groupsFormatted: [],
    selectedHoles: [],
    selectAllHoles: false,
  });

  function formatGeneratedGroups(groups: IShotgunTemplateGroup[]): IShotgunHoleRow[] {
    const groupedArray: IShotgunHoleRow[] = [];

    groups.forEach(group => {
      const holeNumber = parseInt(group.start_hole, 10);
      const existingRow = groupedArray[groupedArray.length - 1];
      if (existingRow && existingRow.holeNumber === holeNumber) {
        existingRow.push(group);
        return;
      }

      const row: IShotgunHoleRow = [];
      row.holeNumber = holeNumber;
      row.checked = false;
      row.push(group);
      groupedArray.push(row);
    });

    return groupedArray;
  }

  const selectedCourse =
    playableCourses.find(course => course.id === state.courseId) ?? playableCourses[0];
  const sharedCourseHoles = playableCourses[0]?.holes ?? facilityStore?.facility?.holes ?? 18;
  const activeCourseHoles = coursesDiffer ? selectedCourse?.holes ?? sharedCourseHoles : sharedCourseHoles;
  const holeCountLimit = coursesDiffer ? activeCourseHoles : facilityStore?.facility?.holes ?? activeCourseHoles;
  const holeLabelMax = shotgunHoleLabelMaxForCount(activeCourseHoles);
  const totalHoles = Array.from({ length: holeCountLimit }, (_, index) => index + 1);

  async function generateGroups() {
    const groupsRes = await PutShotGunTemplateGenerate(
      {
        holes: state.holes,
        direction: state.direction,
        ...(coursesDiffer && selectedCourse ? { course_id: selectedCourse.id } : {}),
      },
      true,
    );
    if (groupsRes.status !== StatusCode.OK) {
      console.log(groupsRes.message);
      return;
    }

    setState(prevState => ({
      ...prevState,
      groups: groupsRes.data,
      groupsFormatted: formatGeneratedGroups(groupsRes.data),
      selectedHoles: [],
      selectAllHoles: false,
    }));
  }

  async function saveNewShotGunTemplate() {
    if (state.title === "" || state.groups.length === 0) {
      return;
    }

    console.log(state);
    const res = await PostShotGunTemplate({ title: state.title, groups: state.groups }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    console.log(res);
    history.push("/admin/settings/tee-sheet/shotgun-templates");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropdownChange(value: number) {
    setState(prevState => ({ ...prevState, holes: value }));
  }

  function handleDirectionChange(value: ShotgunDirection) {
    setState(prevState => ({ ...prevState, direction: value }));
  }

  function handleCourseChange(courseId: number) {
    const course = playableCourses.find(item => item.id === courseId);
    const limit = course?.holes ?? holeCountLimit;
    setState(prevState => ({
      ...prevState,
      courseId,
      holes: prevState.holes > limit ? 1 : prevState.holes,
      groups: [],
      groupsFormatted: [],
      selectedHoles: [],
      selectAllHoles: false,
    }));
  }

  function handleAddHole() {
    const lastHole = state.groupsFormatted[state.groupsFormatted.length - 1]?.holeNumber;
    if (!lastHole || state.groupsFormatted.length >= holeCountLimit) {
      return;
    }

    let nextHole = state.direction === "reverse" ? lastHole - 1 : lastHole + 1;
    if (nextHole < 1) {
      nextHole = holeLabelMax;
    }
    if (nextHole > holeLabelMax) {
      nextHole = 1;
    }

    const groupA: IShotgunTemplateGroup = {
      group_index: nextHole,
      start_hole: nextHole.toString() + "A",
      start_time: "12:00",
    };
    const groupB: IShotgunTemplateGroup = {
      group_index: nextHole,
      start_hole: nextHole.toString() + "B",
      start_time: "12:00",
    };
    const updatedGroups = state.groups;
    updatedGroups.push(groupA);
    updatedGroups.push(groupB);

    const groupedArray = state.groupsFormatted;
    const row: IShotgunHoleRow = [];
    row.holeNumber = nextHole;
    row.checked = false;
    row.push(groupA);
    row.push(groupB);
    groupedArray.push(row);

    setState(prevState => ({ ...prevState, groups: updatedGroups, groupsFormatted: groupedArray }));
  }

  function handleRemoveHole() {
    const groupedArray: any = state.groupsFormatted;

    for (let i = 0; i < state.selectedHoles.length; i++) {
      const holeNumber = groupedArray[state.selectedHoles[i]].holeNumber;
      groupedArray[state.selectedHoles[i]] = [];
      groupedArray[state.selectedHoles[i]].holeNumber = holeNumber;
      groupedArray[state.selectedHoles[i]].checked = false;
    }

    const currentGroups: IShotgunTemplateGroup[] = [].concat(...groupedArray as IShotgunTemplateGroup[]);
    setState(prevState => ({
      ...prevState,
      groups: currentGroups,
      groupsFormatted: groupedArray,
      selectedHoles: [],
    }));
  }

  function handleAddGroup(rowIndex: number) {
    const row = state.groupsFormatted[rowIndex];
    if (!row || row.length >= alphabet.length || row.length >= 4) {
      return;
    }

    const usedLetters = row.map(group => group.start_hole.substring(group.start_hole.length - 1));
    const currentGroupLetter = alphabet.find(letter => !usedLetters.includes(letter));
    if (!currentGroupLetter) {
      return;
    }

    const holeNumber = row.holeNumber ?? parseInt(row[0].start_hole, 10);
    const newGroup: IShotgunTemplateGroup = {
      group_index: holeNumber,
      start_hole: holeNumber.toString() + currentGroupLetter,
      start_time: "12:00",
    };

    const groupedArray = state.groupsFormatted;

    groupedArray[rowIndex].push(newGroup);

    const currentGroups: IShotgunTemplateGroup[] = [].concat(...groupedArray);

    setState(prevState => ({ ...prevState, groups: currentGroups, groupsFormatted: groupedArray }));
  }

  function handleRemoveGroup(holeNum: number) {
    const groupedArray = state.groupsFormatted;

    groupedArray[holeNum].pop();

    const currentGroups: IShotgunTemplateGroup[] = [].concat(...groupedArray);
    setState(prevState => ({ ...prevState, groups: currentGroups, groupsFormatted: groupedArray }));
  }

  const primaryAction = {
    content: t("Save"),
    action: saveNewShotGunTemplate,
  };

  function handleSelectAllHoles(event: any) {
    const { checked } = event.target;

    const groupedArray: any = state.groupsFormatted;

    if (checked) {
      for (let i = 0; i < groupedArray.length; i++) {
        groupedArray[i].checked = true;
      }
    } else {
      for (let i = 0; i < groupedArray.length; i++) {
        groupedArray[i].checked = false;
      }
    }

    setState(prevState => ({ ...prevState, selectAllHoles: !state.selectAllHoles, groupsFormatted: groupedArray }));
  }

  function handleHoleSelectChange(event: any, hole: any, holeNum: any) {
    // setState((prevState) => ({ ...prevState, selectAllHoles: !state.selectAllHoles }));
    const { checked } = event.target;
    const selectedHoles = state.selectedHoles;

    if (checked) {
      selectedHoles.push(holeNum);
    } else {
      const index = selectedHoles.indexOf(holeNum);
      if (index > -1) {
        selectedHoles.splice(index, 1);
      }
    }

    const groupedArray: any = state.groupsFormatted;

    groupedArray[holeNum].checked = !groupedArray[holeNum].checked;

    setState(prevState => ({ ...prevState, groupsFormatted: groupedArray }));
  }

  return (
    <Page
      title={t("New Shotgun Template")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Shotgun Template"),
          url: "/admin/settings/tee-sheet/shotgun-templates",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="flex justify-between gap-4">
            <div style={{ width: "50%" }}>
              <Input
                value={state.title}
                label={t("Title")}
                id="title"
                onChange={handleInputChange}
                placeholder={t("Title")}
              />
            </div>

            <div className="flex gap-4">
              {coursesDiffer && (
                <Select
                  label={t("Course")}
                  onChange={handleCourseChange}
                  defaultValue={selectedCourse?.id}
                >
                  {playableCourses.map(course => {
                    return (
                      <Option key={course.id} value={course.id}>
                        <span>{course.full_name || course.long_name}</span>
                      </Option>
                    );
                  })}
                </Select>
              )}
              <Select label={t("Direction")} onChange={handleDirectionChange} defaultValue={state.direction}>
                <Option value="regular">
                  <span>{t("Regular")}</span>
                </Option>
                <Option value="reverse">
                  <span>{t("Reverse")}</span>
                </Option>
              </Select>
              <Select
                key={selectedCourse?.id ?? "holes"}
                label={t("Select the Number of Holes")}
                onChange={handleDropdownChange}
                defaultValue={state.holes > holeCountLimit ? 1 : state.holes}
              >
                {totalHoles.map((hole: number, index: number) => {
                  return (
                    <Option key={index} value={hole}>
                      <span>{hole}</span>
                    </Option>
                  );
                })}
              </Select>
            </div>
          </div>

          <div className="mt-4" style={{ display: "flex", justifyContent: "end" }}>
            <Button type="primary" onClick={generateGroups}>
              <span>{t("Generate")}</span>
            </Button>
          </div>
        </Card.Section>
      </Card>

      {state.groupsFormatted.length >= 1 && (
        <div className="mt-4">
          {/* <div className="shotgun-template_table_header_container">
    <h1>Shotgun</h1>
    <p>Customize shotgun template</p>
  </div> */}

          <table className="ui-table shotgun-template_table">
            <thead>
              <tr className="">
                <td style={{ width: "15%" }}>
                  <div className="flex">
                    <Checkbox size="medium" value={1} onChange={handleSelectAllHoles} checked={state.selectAllHoles} />
                    {t("Holes")}
                  </div>
                </td>
                <td>{t("Groups")}</td>
                <td style={{ width: "15%" }}></td>
                <td style={{ width: "20%", textAlign: "center" }}>
                  <div title={t("Add group")} onClick={handleRemoveHole} className="shotgun-template_add_button">
                    Delete &nbsp; <FontAwesomeIcon icon={faMinus} />
                  </div>
                </td>
              </tr>
            </thead>
            <tbody className="shotgun-template_table_body">
              {state.groupsFormatted?.map((hole: any, holeNum: number) => {
                return (
                  <tr key={holeNum}>
                    <td style={{ borderBottomStyle: "none", padding: "0px 24px" }}>
                      <div className="flex items-center gap-4">
                        <div className="flex">
                          <Checkbox
                            size="medium"
                            value={1}
                            onChange={event => handleHoleSelectChange(event, hole, holeNum)}
                            checked={hole.checked}
                          />
                        </div>

                        <div className="shotgun-template_table_text">{hole.holeNumber ?? holeNum + 1}</div>
                      </div>
                    </td>

                    <td
                      className="shotgun-template_table_body_holes_container"
                      style={{
                        borderBottomStyle: "none",
                        padding: "0px 24px",
                      }}
                    >
                      {hole.map((group: IShotgunTemplateGroup, index: number) => {
                        return (
                          <div key={index} className="shotgun-template_table_body_holes">
                            <div>{group.start_hole.substring(group.start_hole.length - 1)}</div>
                          </div>
                        );
                      })}
                    </td>

                    <td style={{ borderBottomStyle: "none", padding: "0px 0px" }}>
                      {state.groupsFormatted[holeNum].length < alphabet.length && (
                        <div className="shotgun-template_add_button">
                          <Button onClick={() => handleAddGroup(holeNum)} type="text" size="small">
                            <FontAwesomeIcon icon={faPlus} /> &nbsp; Add group
                          </Button>
                        </div>
                      )}
                    </td>

                    <td style={{ borderBottomStyle: "none", padding: "0px 0px" }}>
                      {state.groupsFormatted[holeNum].length < alphabet.length && (
                        <div className="shotgun-template_add_button">
                          <Button
                            onClick={() => handleRemoveGroup(holeNum)}
                            type="text"
                            size="small"
                            disabled={hole.length === 0}
                          >
                            <FontAwesomeIcon icon={faMinus} /> &nbsp; Remove group
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {state.groupsFormatted.length < holeCountLimit && (
            <div title={t("Add hole")} className="shotgun-template_add_button mt-3" style={{ justifyContent: "left" }}>
              <Button onClick={handleAddHole} type="text" size="small">
                <FontAwesomeIcon icon={faPlus} /> &nbsp; Add hole
              </Button>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
